import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import type { VivestSelectOption } from '@vivest-dev/design-system-web-components';
import { RESGATE_KEY } from '../../../core/constants/storage-keys';
import { TIPO_INSTITUTO } from '../../../core/constants/tipo-instituto';
import { VivestControl } from '../../../core/directives/vivest-control';
import { BancoResponse, PaisResponse } from '../../../core/models/dados-cadastrais';
import { EscolhaResgate, RegimeTributacao } from '../../../core/models/resgate';
import { HttpClientService } from '../../../shared/services/http-client-service';
import { LoaderService } from '../../../shared/services/loader-service';
import { LocalStorageService } from '../../../shared/services/local-storage-service';
import { ToastService } from '../../../shared/services/toast-service';

interface RegimeOpcao {
  id: RegimeTributacao;
  titulo: string;
}

/** O resgate pode ser recebido de 1 a 12 parcelas. */
const MAX_PARCELAS = 12;

@Component({
  imports: [ReactiveFormsModule, VivestControl],
  selector: 'app-resgate-mvp',
  styleUrl: './resgate-mvp.scss',
  templateUrl: './resgate-mvp.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ResgateMvp {
  private readonly router = inject(Router);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly loaderService = inject(LoaderService);
  private readonly httpClientService = inject(HttpClientService);
  private readonly toastService = inject(ToastService);

  protected readonly bancos = signal<VivestSelectOption[]>([]);
  protected readonly paises = signal<VivestSelectOption[]>([]);
  protected readonly parcelas: VivestSelectOption[] = Array.from({ length: MAX_PARCELAS }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  }));
  protected readonly regimes: RegimeOpcao[] = [
    { id: 'progressivo', titulo: 'Progressivo' },
    { id: 'regressivo', titulo: 'Regressivo' },
  ];

  /** Campos digitados/selecionados; o VivestControl liga os Web Components ao form e aplica o vivestFormat. */
  protected readonly form = this.formBuilder.nonNullable.group({
    banco: [''],
    agencia: [''],
    conta: [''],
    parcelas: [''],
    pais: [''],
    zipCode: [''],
    nif: [''],
  });
  private readonly valores = toSignal(this.form.valueChanges, { initialValue: this.form.getRawValue() });

  // Dados bancários
  protected readonly banco = computed(() => this.valores().banco ?? '');
  protected readonly agencia = computed(() => this.valores().agencia ?? '');
  protected readonly conta = computed(() => this.valores().conta ?? '');

  // Tributação e parcelas
  protected readonly regime = signal<RegimeTributacao | null>(null);
  protected readonly quantidadeParcelas = computed(() => this.valores().parcelas ?? '');

  // Residência no exterior
  protected readonly resideExterior = signal<boolean | null>(null);
  protected readonly pais = computed(() => this.valores().pais ?? '');
  protected readonly zipCode = computed(() => this.valores().zipCode ?? '');
  protected readonly nif = computed(() => this.valores().nif ?? '');

  protected readonly podeContinuar = computed(() => {
    const dadosBancarios = !!this.banco() && !!this.agencia().trim() && !!this.conta().trim();
    const tributacao = !!this.regime() && !!this.quantidadeParcelas();
    const residencia =
      this.resideExterior() === false ||
      (this.resideExterior() === true && !!this.pais() && !!this.zipCode().trim() && !!this.nif().trim());
    return dadosBancarios && tributacao && residencia;
  });

  ngOnInit(): void {
    void this.carregarListas();
  }

  /** Busca as listas de bancos e países no mesmo endpoint dos dados cadastrais. */
  protected async carregarListas(): Promise<void> {
    this.loaderService.showLoader('Carregando...');

    try {
      const { body } = await this.httpClientService.getDadosCadastrais();
      if (!body) return;

      this.bancos.set(body.bancos.map(toSelectOption));
      this.paises.set(body.paises.map(toSelectOption));
    } catch {
      this.toastService.show('Não foi possível carregar as informações. Tente novamente.', 'error');
    } finally {
      this.loaderService.hideLoader();
    }
  }

  protected selecionarRegime(regime: RegimeTributacao): void {
    this.regime.set(regime);
  }

  protected onRegimeChange(event: Event, regime: RegimeTributacao): void {
    if ((event.target as HTMLInputElement).checked) {
      this.selecionarRegime(regime);
    }
  }

  protected onResideExteriorChange(event: Event, reside: boolean): void {
    if ((event.target as HTMLInputElement).checked) {
      this.resideExterior.set(reside);
    }
  }

  onReturn(): void {
    this.router.navigate(['/instituto-online/escolha-instituto']);
  }

  /** Salva as escolhas no localStorage (RESGATE_KEY) e segue para a assinatura do termo. */
  onSubmit(): void {
    if (!this.podeContinuar()) {
      return;
    }
    const resideExterior = this.resideExterior() === true;

    this.localStorageService.addObjectStorageEcrypted<EscolhaResgate>(
      {
        idBanco: Number(this.banco()),
        agencia: this.agencia().trim(),
        conta: this.conta().trim(),
        regime: this.regime()!,
        parcelas: Number(this.quantidadeParcelas()),
        resideExterior,
        idPais: resideExterior ? Number(this.pais()) : null,
        zipCode: resideExterior ? this.zipCode().trim() : null,
        nif: resideExterior ? this.nif().trim() : null,
      },
      RESGATE_KEY,
    );
    this.router.navigate(['/instituto-online/assinatura-termo-mvp'], {
      queryParams: { idInstituto: TIPO_INSTITUTO.RESGATE },
    });
  }
}

function toSelectOption({ id, descricao }: BancoResponse | PaisResponse): VivestSelectOption {
  return { value: String(id), label: descricao };
}
